/**
 * 차량 메시의 "역할" 이름.
 *
 * 별도 파일로 떼어 둔 이유는 두 방향에서 함께 쓰이기 때문입니다.
 *   - 데이터 쪽(data/carModels.ts): 모델별로 어떤 메시가 어떤 역할인지 선언
 *   - 렌더 쪽(applyCarConfiguration.ts): 역할별로 머티리얼을 만들어 할당
 * 이걸 한쪽에 두면 데이터가 렌더 모듈을, 또는 그 반대를 import하게 되어 방향이 꼬입니다.
 */
export const MESH_ROLES = {
  PAINT: 'paint',
  GLASS: 'glass',
  RIM: 'rim',
  SEAT: 'seat',
  TRIM: 'trim',
  // 실내 디스플레이(인포테인먼트 화면). 조명과 무관하게 스스로 빛나야 합니다.
  SCREEN: 'screen',
  /*
   * 번호판. 에셋이 들고 온 외국 번호판 텍스처를 쇼룸 로고 판으로 갈아끼웁니다.
   *
   * 화면(SCREEN)과 역할을 나눈 이유는 재질이 반대이기 때문입니다. 화면은 스스로
   * 빛나는 물체라 MeshBasicMaterial + toneMapped:false를 쓰지만, 번호판은 빛을 받는
   * 평범한 판이라 같은 처리를 하면 어두운 스튜디오에서 판만 발광하는 표지판이 됩니다.
   * 텍스처 비율도 화면(16:10 안팎)과 번호판(2:1~4.6:1)이 크게 달라서 한 설정으로
   * 묶으면 어느 한쪽의 로고가 찌그러집니다.
   */
  PLATE: 'plate',
  /*
   * 조각 부조로 새겨진 엠블럼. 색이 아니라 광택이 필요한 역할입니다.
   * 무광 머티리얼로는 부조의 요철이 하이라이트를 만들지 못해 평평하게 보입니다.
   */
  EMBLEM: 'emblem',
} as const;

/** MESH_ROLES가 취할 수 있는 값. 오타를 컴파일 시점에 잡습니다. */
export type MeshRole = (typeof MESH_ROLES)[keyof typeof MESH_ROLES];

/**
 * 역할에 속하는 메시를 고르는 방법.
 *
 * 두 가지인 이유는 data/carModels.ts의 selectors 주석을 참고하세요. 요약하면,
 * 노드 이름이 자동 생성된 에셋에서는 머티리얼 이름이 유일한 단서인데 머티리얼이
 * 여러 부위에 공유되는 경우가 있어 그때는 메시 이름으로 좁혀야 합니다.
 */
export interface MeshSelector {
  meshes?: string[];
  materials?: string[];
}

/**
 * 역할 -> 선택자. 모든 모델이 모든 역할을 갖추지는 못하므로 부분 맵입니다.
 */
export type MeshSelectorMap = { [role in MeshRole]?: MeshSelector };

/**
 * 모든 모델이 모든 역할을 갖추지는 못합니다.
 * 예를 들어 실내 지오메트리가 없는 외관 전용 모델은 seat/trim이 없고,
 * 디스플레이가 실내 머티리얼에 병합된 모델은 screen이 없습니다.
 *
 * 그래서 "반드시 있어야 하는 역할"만 따로 정해 둡니다.
 * 이 둘이 비면 차가 색이 안 변하거나 유리가 불투명해서 쇼룸이 성립하지 않습니다.
 */
export const REQUIRED_MESH_ROLES: readonly MeshRole[] = [MESH_ROLES.PAINT, MESH_ROLES.GLASS];

/**
 * 선택자(selector)가 지정한 이름들을 한 배열로 펼칩니다.
 * 비어 있는 역할을 판정할 때 여러 곳에서 필요해서 여기 둡니다.
 */
export function selectorNames(selector: MeshSelector | undefined | null): string[] {
  if (selector === undefined || selector === null) {
    return [];
  }
  return [...(selector.meshes ?? []), ...(selector.materials ?? [])];
}
